import React from "react";

import styled, { css } from "styled-components";

import { EncryptedValue } from "./EncryptedValue";

import * as DetailList from "~/components/DetailList";
import { TransactionWrap } from "~/hooks/use-transaction";
import { DetailListItem } from "~/templates/transactions/components/TransactionDetailsInfo/components/TransactionBeneficiary/TransactionBeneficiary";

interface TextProps {
  type?: "not-required" | "missing";
}

export const PIIDataText = styled.text<TextProps>`
  font-style: "Montserrat";
  font-weight: 400;
  font-size: 14px;
  line-weight: 14.63px;
  ${(props) =>
    props.type === "not-required" &&
    css`
      color: #8e8e8e;
      font-size: 12px;
    `}
  ${(props) =>
    props.type === "missing" &&
    css`
      color: #f45b69;
      font-size: 12px;
    `}
`;

const OptionalLabel = styled.span`
  font-weight: normal;
  text-transform: lowercase;
`;

const WarningLabel = styled.span`
  color: orange;
`;

interface FieldsProps {
  fields: DetailListItem[];
  requiredFields: string[];
  transaction: TransactionWrap;
  direction?: string;
  warnings?: string[];
  isEncryptionEnabled: boolean;
}

const PIIFields = ({ fields, requiredFields, transaction, direction, warnings, isEncryptionEnabled }: FieldsProps) => (
  <DetailList.Content>
    {fields.map(({ label, value, isEncrypted = true, mobile = 12, tablet = 6, desktop = 3, ref = "", warning }) => {
      const isTxIncomingAndFieldRequired = direction === "incoming" && requiredFields.includes(ref);
      const missingDataText = isTxIncomingAndFieldRequired ? "missing required data" : "not required";
      const missingDataStyle = isTxIncomingAndFieldRequired ? "missing" : "not-required";

      const requiredCounterpartyWarningText = <WarningLabel>{"required in counterparty's jurisdiction"}</WarningLabel>;

      const showAsWarning = warning && warnings && warnings.includes(warning) && direction === "outgoing";

      const fieldValue = value || ((transaction as unknown) as Record<string, string>)[ref];
      const valueToShow = fieldValue ? (
        isEncryptionEnabled && isEncrypted ? (
          <EncryptedValue />
        ) : (
          fieldValue
        )
      ) : (
        missingDataText
      );

      const textStyle = fieldValue ? undefined : missingDataStyle;

      const optionalLabel =
        warning && warnings && warnings.includes(warning) ? (
          <OptionalLabel title="This data is required in your counterparty VASP's jurisdiction but not in yours.">
            optional&nbsp;&#128712;
          </OptionalLabel>
        ) : null;

      return (
        <DetailList.Item key={label} mobile={mobile} tablet={tablet} desktop={desktop}>
          <DetailList.Term>
            {label} {optionalLabel}
          </DetailList.Term>

          <DetailList.Description>
            <PIIDataText type={textStyle} data-dd-privacy="mask">
              {showAsWarning ? requiredCounterpartyWarningText : valueToShow}
            </PIIDataText>
          </DetailList.Description>
        </DetailList.Item>
      );
    })}
  </DetailList.Content>
);

export default PIIFields;
